import { useSlidingIndicator } from "@components/animations";
import VersionSwitch from "@components/layout/VersionSwitch";
import { layout } from "@demo-data/layout";
import { LogoImg } from "@images/svg";
import MenuIcon from "@mui/icons-material/Menu";
import { CssBaseline, Menu, MenuItem, Typography } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { Container, Stack, useTheme } from "@mui/system";
import { hexToRGBA } from "@utils/hex-to-rgba";
import { readableOn } from "@utils/readable-on";
import * as React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Footer from "../footer";
import Sidebar from "../sidebar";
import Settings from "./settings";
const drawerWidth = 50;

function DrawerAppBar(
  { children }: { children: React.ReactNode },
  props?: DrawerAppBarProps,
) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { routes, logoTitle } = layout?.header || {};
  const { window } = props || {};
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Sliding active-section indicator for the desktop nav.
  const navLinksRef = React.useRef<HTMLDivElement>(null);
  const activePath =
    routes.find((route) =>
      route.path === "/" ? pathname === "/" : pathname.startsWith(route.path),
    )?.path ??
    routes[0]?.path ??
    "/";
  const navIndicatorStyle = useSlidingIndicator(navLinksRef, activePath);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      //  onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent={"center"}
        spacing={1}
        sx={{
          cursor: "pointer",
        }}
        component={Link}
        to="/"
        py={4}
      >
        <LogoImg iconColor={theme.palette.text.primary} />
        <Typography variant="h4" fontWeight={800}>
          {logoTitle}
        </Typography>
      </Stack>

      <Divider />
      <Stack
        gap={2}
        fontSize="1.2rem"
        textAlign={"left"}
        py={1}
        mt={2}
        onClick={handleDrawerToggle}
      >
        {routes.map((item, index) => (
          <MenuItem
            key={item.id}
            className={mobileOpen ? "anim-menu-item is-in" : "anim-menu-item"}
            style={{ "--anim-delay": `${index * 45}ms` } as React.CSSProperties}
            sx={{
              backgroundColor:
                pathname === item.path
                  ? hexToRGBA(theme.palette.background.paper, 0.3)
                  : "transparent",
              borderRight:
                pathname === item.path
                  ? `4px solid ${theme.palette.divider}`
                  : "none",
              color:
                pathname === item.path
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              fontFamily: "FiraCode",
              fontWeight: pathname === item.path ? 600 : 400,
              padding: "0.5rem 1rem",
              "&:hover": {
                backgroundColor: hexToRGBA(theme.palette.background.paper, 0.3),
                color: theme.palette.text.primary,
                borderRight: `4px solid ${theme.palette.divider}`,
              },
            }}
            component={Link}
            to={item.path}
          >
            <Box
              component={"span"}
              color={(theme) => theme.palette.primary.main}
            >
              #
            </Box>
            {item.name}
          </MenuItem>
        ))}
      </Stack>
    </Box>
  );

  // Theme-driven CSS variables live on :root (not an inline style on the layout
  // box) so that portalled UI — the settings drawer, menus — inherits them too.
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const vars: Record<string, string> = {
      "--anim-accent": theme.palette.primary.main,
      "--anim-accent-contrast": readableOn(theme.palette.primary.main),
      "--anim-accent-soft": hexToRGBA(theme.palette.primary.main, 0.45),
      "--anim-chip-bg": hexToRGBA(theme.palette.primary.main, 0.12),
      "--anim-subtle-bg": hexToRGBA(theme.palette.text.primary, 0.04),
      "--anim-secondary": theme.palette.secondary.main,
      "--anim-text": theme.palette.text.primary,
      "--anim-muted": theme.palette.text.secondary,
      "--anim-border": theme.palette.divider,
      "--anim-bg": theme.palette.background.default,
      "--anim-surface": hexToRGBA(
        theme.palette.background.paper,
        theme.palette.mode === "dark" ? 0.08 : 0.7,
      ),
    };
    Object.entries(vars).forEach(([key, value]) =>
      root.style.setProperty(key, value),
    );
    return () => {
      Object.keys(vars).forEach((key) => root.style.removeProperty(key));
    };
  }, [theme]);

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        // Scroll-reveal elements start translated ±38px, which widens the
        // scrollable area and produced a horizontal scrollbar on load
        // (measured 18px at 390px, 10px at 768/1024px). `clip` contains it
        // without creating a scroll container, unlike `hidden`.
        overflowX: "clip",
      }}
    >
      {/* Page-wide animated gradient background (behind all content). */}
      <Box className="anim-ambient anim-ambient--fixed" aria-hidden="true">
        <span className="anim-ambient__blob anim-ambient__blob--a" />
        <span className="anim-ambient__blob anim-ambient__blob--b" />
        <span className="anim-ambient__blob anim-ambient__blob--c" />
        <span className="anim-ambient__grid" />
      </Box>
      <AppBar
        component="nav"
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          boxShadow: "none",
          pl: {
            md: 7,
            xs: 0,
          },
          backgroundImage: "none",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className="anim-icon-btn"
              sx={{
                mr: 2,
                display: { md: "none" },
                color: (theme) => theme.palette.text.primary,
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }}
              component="div"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  cursor: "pointer",
                }}
                component={Link}
                to="/"
              >
                <LogoImg iconColor={theme.palette.text.primary} />
                <Typography
                  variant="body1"
                  fontWeight={800}
                  color="text.primary"
                >
                  Abdul Kader
                </Typography>
              </Stack>
            </Box>

            <Stack
              ref={navLinksRef}
              sx={{
                display: { xs: "none", md: "block" },
                position: "relative",
              }}
              spacing={2}
              direction="row"
            >
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                {/* <ModeList /> */}
              </Menu>
              {routes.map((route, index) => {
                return (
                  <NavLink
                    to={route.path}
                    key={index}
                    data-indicator-item={route.path}
                    className={({ isActive }) =>
                      `anim-nav-link${isActive ? " is-active" : ""}`
                    }
                  >
                    <span className="anim-nav-hash">#</span>
                    <span>{route.name}</span>
                  </NavLink>
                );
              })}
              <Box
                className="anim-nav-indicator"
                aria-hidden="true"
                style={navIndicatorStyle}
              />
            </Stack>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                width: {
                  xs: "100%",
                  md: "fit-content",
                },
                px: 2,
              }}
            >
              <VersionSwitch />
              <Settings />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="nav">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          PaperProps={{
            sx: {
              backgroundColor: (theme) => theme.palette.background.default,
            },
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: `${drawerWidth}%`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: 50,
          left: 20,
          // Above `main` (z-index: 1, which hosts the animated background
          // layer) so the social icons stay visible AND clickable.
          zIndex: 2,
        }}
      >
        <Sidebar />
      </Box>
      <Box
        component="main"
        sx={{
          // p: 2,
          // pr: 3,
          // No solid background here — the fixed animated background layer
          // paints the theme colour so the effect stays visible behind content.
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        <Toolbar />
        <Box
          sx={{
            pl: {
              xs: 4,
              md: 8,
            },
          }}
        >
          <CssBaseline enableColorScheme />
          <Outlet />
          {children}
          <Box mb={8} />

          <Footer />
        </Box>
      </Box>
    </Box>
  );
}

export default DrawerAppBar;
